import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import type { AnalyticsRange } from "../../stats/api/statsApi";
import { useEventCountsQuery } from "../../stats/hooks/useStats";

interface FunnelStep {
  key: string;
  label: string;
  count: number;
}

interface FunnelGuestViewProps {
  range: AnalyticsRange;
}

const formatPct = (value: number) =>
  Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : "—";

// Eventos específicos del guest. pwa_session_started y pwa_installed son
// guest-exclusive (la extension no los dispara). webrtc_pairing_completed y
// stream_started están planeados — si todavía no se dispara, el contador
// queda en 0 y se ve la caída en el funnel.
const GUEST_EVENT_NAMES = new Set([
  "pwa_session_started",
  "pwa_installed",
  "webrtc_pairing_completed",
  "stream_started",
  // signup/login disparados desde el guest llevan source: "guest_pwa" en
  // event-data — el counter global de signup_completed mezcla web+guest;
  // los excluimos del funnel guest hasta tener filtro por property.
]);

export const FunnelGuestView = ({ range }: FunnelGuestViewProps) => {
  const { t } = useTranslation("common");
  const eventsQuery = useEventCountsQuery(range);

  const steps = useMemo<FunnelStep[]>(() => {
    const events = eventsQuery.data ?? [];
    const lookup = new Map(events.map((e) => [e.name, e.count]));
    return [
      {
        key: "pwa_session_started",
        label: t("FUNNEL_GUEST.steps.session"),
        count: lookup.get("pwa_session_started") ?? 0,
      },
      {
        key: "pwa_installed",
        label: t("FUNNEL_GUEST.steps.installed"),
        count: lookup.get("pwa_installed") ?? 0,
      },
      {
        key: "webrtc_pairing_completed",
        label: t("FUNNEL_GUEST.steps.paired"),
        count: lookup.get("webrtc_pairing_completed") ?? 0,
      },
      {
        key: "stream_started",
        label: t("FUNNEL_GUEST.steps.streamed"),
        count: lookup.get("stream_started") ?? 0,
      },
    ];
  }, [eventsQuery.data, t]);

  const guestEvents = useMemo(() => {
    return (eventsQuery.data ?? []).filter((e) =>
      GUEST_EVENT_NAMES.has(e.name),
    );
  }, [eventsQuery.data]);

  const isLoading = eventsQuery.isLoading;
  const isFetching = eventsQuery.isFetching;

  const top = steps[0]?.count ?? 0;

  return (
    <div className="relative space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">
            {t("FUNNEL_GUEST.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading
            ? Array.from({ length: 4 }).map((_, idx) => (
                <Skeleton key={idx} className="h-10 w-full" />
              ))
            : steps.map((step, idx) => {
                const widthPct = top > 0 ? (step.count / top) * 100 : 0;
                const prev = idx > 0 ? steps[idx - 1].count : null;
                const stepRate =
                  prev != null && prev > 0 ? step.count / prev : null;
                const overallRate = top > 0 ? step.count / top : null;
                return (
                  <div key={step.key} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-neutral-100">
                        {step.label}
                      </span>
                      <span className="tabular-nums text-neutral-300">
                        {step.count.toLocaleString("es-AR")}
                        {stepRate != null && (
                          <span className="ml-2 text-xs text-neutral-500">
                            {formatPct(stepRate)} {t("FUNNEL.fromPrev")}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="relative h-8 w-full overflow-hidden rounded-md bg-neutral-800/60">
                      <div
                        className="h-full bg-gradient-to-r from-cyan-500/60 to-cyan-500/30 transition-[width] duration-500"
                        style={{ width: `${Math.max(widthPct, 2)}%` }}
                      />
                      {overallRate != null && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-neutral-400 tabular-nums">
                          {formatPct(overallRate)} {t("FUNNEL.fromTop")}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">
            {t("FUNNEL_GUEST.allEvents")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("FUNNEL.event")}</TableHead>
                <TableHead className="text-right">
                  {t("FUNNEL.count")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 3 }).map((_, idx) => (
                  <TableRow key={`skeleton-${idx}`}>
                    <TableCell>
                      <Skeleton className="h-4 w-3/4" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="ml-auto h-4 w-12" />
                    </TableCell>
                  </TableRow>
                ))
              ) : guestEvents.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={2}
                    className="text-center text-muted-foreground"
                  >
                    {t("FUNNEL_GUEST.noEvents")}
                  </TableCell>
                </TableRow>
              ) : (
                guestEvents.map((event) => (
                  <TableRow key={event.name}>
                    <TableCell className="font-mono text-xs">
                      {event.name}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {event.count.toLocaleString("es-AR")}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {isFetching && !isLoading ? <LoadingOverlay /> : null}
    </div>
  );
};
